import { router, usePage } from "@inertiajs/react";
import type { ParserMap, UseQueryStatesOptions } from "nuqs";
import {
  unstable_createAdapterProvider as createAdapterProvider,
  renderQueryString,
} from "nuqs/adapters/custom";
import type {
  unstable_AdapterInterface as AdapterInterface,
  unstable_AdapterOptions as AdapterOptions,
  unstable_UpdateUrlFunction as UpdateUrlFunction,
} from "nuqs/adapters/custom";
import { useState } from "react";

import { useMountEffect } from "@/hooks/use-mount-effect";
import { index as documentsIndex } from "@/routes/documents";
import { index as worksIndex } from "@/routes/works";

type DefaultedQueryParsers = Record<string, { readonly defaultValue: unknown }>;
const INERTIA_URL_BASE = "http://inertia.local";
const WORKS_COLLECTION_PATH = worksIndex.url();
const WORKS_QUERY_STATE_PROPS = [
  "works",
] as const satisfies readonly (keyof App.Data.Pages.Works.WorkIndexPageData)[];
const DOCUMENTS_COLLECTION_PATH = documentsIndex.url();
const DOCUMENTS_QUERY_STATE_PROPS = [
  "documents",
  "filters",
  "sort",
  "totalCount",
  "tagOptions",
] as const satisfies readonly (keyof App.Data.Pages.Documents.DocumentIndexPageData)[];

interface QueryStateVisitOptions {
  readonly only?: string[];
  readonly reset?: string[];
}

export const withInertiaQueryStateOptions = <Parsers extends ParserMap>(
  options: Readonly<Partial<UseQueryStatesOptions<Parsers>>> = {},
): Partial<UseQueryStatesOptions<Parsers>> => ({
  history: "replace",
  scroll: false,
  shallow: false,
  ...options,
});

/**
 * Array-valued parsers compare element-wise: nuqs happens to return its own
 * defaultValue reference when the param is absent, but relying on that
 * identity makes default detection silently wrong for any caller (or test)
 * that supplies an equal-but-distinct array.
 */
export const matchesDefaultValue = (value: unknown, defaultValue: unknown): boolean => {
  if (Array.isArray(value) && Array.isArray(defaultValue)) {
    return (
      value.length === defaultValue.length &&
      value.every((item, index) => item === defaultValue[index])
    );
  }

  return value === defaultValue;
};

export const hasActiveQueryState = <Parsers extends DefaultedQueryParsers>(
  parsers: Parsers,
  values: {
    readonly [Key in keyof Parsers]: unknown;
  },
): boolean => {
  for (const [key, parser] of Object.entries(parsers)) {
    if (!matchesDefaultValue(values[key], parser.defaultValue)) {
      return true;
    }
  }

  return false;
};

const searchParamsFromInertiaUrl = (url: string): URLSearchParams =>
  new URL(url, INERTIA_URL_BASE).searchParams;

const urlFromVisitTarget = (url: string | URL): URL => new URL(url.toString(), INERTIA_URL_BASE);

const normalizedPath = (url: URL): string => url.pathname.replace(/\/+$/u, "");

export const queryStateVisitOptions = (visitTarget: string | URL): QueryStateVisitOptions => {
  const visitUrl = urlFromVisitTarget(visitTarget);
  const path = normalizedPath(visitUrl);

  if (path === WORKS_COLLECTION_PATH) {
    return {
      only: [...WORKS_QUERY_STATE_PROPS],
      reset: ["works"],
    };
  }

  if (path === DOCUMENTS_COLLECTION_PATH) {
    return {
      only: [...DOCUMENTS_QUERY_STATE_PROPS],
      reset: ["documents"],
    };
  }

  return {};
};

const browserUrl = (): URL => {
  if (typeof window === "undefined") {
    throw new TypeError("nuqs URL updates require a browser environment.");
  }

  return new URL(window.location.href);
};

const useNuqsInertiaAdapter = (): AdapterInterface => {
  const currentUrl = usePage().url;

  const [searchParams, setSearchParams] = useState(() => searchParamsFromInertiaUrl(currentUrl));

  useMountEffect(() => {
    const syncBrowserUrl = () => {
      setSearchParams(browserUrl().searchParams);
    };

    const removeNavigateListener = router.on("navigate", (event) => {
      setSearchParams(searchParamsFromInertiaUrl(event.detail.page.url));
    });
    window.addEventListener("popstate", syncBrowserUrl);

    return () => {
      removeNavigateListener();
      window.removeEventListener("popstate", syncBrowserUrl);
    };
  });

  const updateUrl: UpdateUrlFunction = (
    search: Readonly<URLSearchParams>,
    options: AdapterOptions,
  ) => {
    const currentBrowserUrl = browserUrl();
    const url = new URL(currentBrowserUrl);
    url.search = renderQueryString(search);

    setSearchParams(url.searchParams);

    const preserveScroll = options.scroll !== true;

    if (options.shallow === false) {
      router.visit(url, {
        async: true,
        preserveScroll,
        preserveState: true,
        replace: options.history === "replace",
        ...queryStateVisitOptions(url),
      });

      return;
    }

    const clientVisit = {
      clearHistory: false,
      encryptHistory: false,
      preserveScroll,
      preserveState: true,
      url: url.toString(),
    };

    if (options.history === "push") {
      router.push(clientVisit);

      return;
    }

    router.replace(clientVisit);
  };

  return {
    searchParams,
    updateUrl,
  };
};

export const NuqsAdapter = createAdapterProvider(useNuqsInertiaAdapter);
